import { supabase } from '../lib/supabase';
import { SecurityService } from '../lib/securityService';

// ============================================================================
// SCRIPT DE MIGRATION POUR LE CHIFFREMENT DES CLÉS API
// ============================================================================

/**
 * Interface pour les clés API existantes
 */
interface ExistingApiKey {
  id: string;
  service: string;
  api_key: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
}

/**
 * Résultat de la migration
 */
interface MigrationResult {
  total: number;
  migrated: number;
  alreadyEncrypted: number;
  errors: number;
  errorDetails: Array<{ service: string; error: string }>;
}

/**
 * Vérifie si une clé API est déjà chiffrée
 */
async function isKeyEncrypted(apiKey: string): Promise<boolean> {
  try {
    // Tente de déchiffrer la clé
    await SecurityService.decryptApiKey(apiKey);
    return true;
  } catch {
    // Si le déchiffrement échoue, la clé n'est pas chiffrée
    return false;
  }
}

/**
 * Migre une clé API vers le format chiffré
 */
async function migrateApiKey(apiKeyRecord: ExistingApiKey): Promise<{ success: boolean; error?: string }> {
  try {

    // Vérifie si la clé est déjà chiffrée
    const alreadyEncrypted = await isKeyEncrypted(apiKeyRecord.api_key);
    if (alreadyEncrypted) {
      return { success: true };
    }

    // Valide et nettoie la clé API
    const sanitizedKey = SecurityService.sanitizeApiKey(apiKeyRecord.api_key);
    
    // Chiffre la clé API
    const encryptedKey = await SecurityService.encryptApiKey(sanitizedKey);

    // Met à jour la clé dans la base de données
    const { error } = await supabase
      .from('api_keys')
      .update({
        api_key: encryptedKey,
        updated_at: new Date().toISOString()
      })
      .eq('id', apiKeyRecord.id);

    if (error) {
      console.error(`Erreur lors de la mise à jour de la clé API pour ${apiKeyRecord.service}:`, error);
      return { success: false, error: error.message };
    }

    return { success: true };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
    console.error(`❌ Erreur lors de la migration de la clé API pour ${apiKeyRecord.service}:`, errorMessage);
    return { success: false, error: errorMessage };
  }
}

/**
 * Fonction principale de migration
 */
export async function migrateApiKeysToEncryption(): Promise<MigrationResult> {

  const result: MigrationResult = {
    total: 0,
    migrated: 0,
    alreadyEncrypted: 0,
    errors: 0,
    errorDetails: []
  };

  try {
    // Récupère toutes les clés API existantes
    const { data: apiKeys, error: fetchError } = await supabase
      .from('api_keys')
      .select('*');

    if (fetchError) {
      console.error('❌ Erreur lors de la récupération des clés API:', fetchError);
      throw new Error(`Impossible de récupérer les clés API: ${fetchError.message}`);
    }

    if (!apiKeys || apiKeys.length === 0) {
      return result;
    }

    result.total = apiKeys.length;

    // Migre chaque clé API
    for (const apiKey of apiKeys) {
      try {
        // Vérifie d'abord si la clé est déjà chiffrée
        const alreadyEncrypted = await isKeyEncrypted(apiKey.api_key);
        
        if (alreadyEncrypted) {
          result.alreadyEncrypted++;
          continue;
        }

        // Migre la clé
        const migrationResult = await migrateApiKey(apiKey);
        
        if (migrationResult.success) {
          result.migrated++;
        } else {
          result.errors++;
          result.errorDetails.push({
            service: apiKey.service,
            error: migrationResult.error || 'Erreur inconnue'
          });
        }

        // Petite pause pour éviter de surcharger la base de données
        await new Promise(resolve => setTimeout(resolve, 100));

      } catch (error) {
        result.errors++;
        const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
        result.errorDetails.push({
          service: apiKey.service,
          error: errorMessage
        });
        console.error(`❌ Erreur lors du traitement de la clé API pour ${apiKey.service}:`, errorMessage);
      }
    }

    // Affiche le résumé

    if (result.errorDetails.length > 0) {
      result.errorDetails.forEach(({ service, error }) => {
      });
    }

    if (result.errors === 0) {
    } else {
    }

    return result;

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
    console.error('❌ Erreur fatale lors de la migration:', errorMessage);
    
    result.errors++;
    result.errorDetails.push({
      service: 'GLOBAL',
      error: errorMessage
    });

    return result;
  }
}

/**
 * Fonction pour vérifier l'état du chiffrement des clés API
 */
export async function checkEncryptionStatus(): Promise<{
  total: number;
  encrypted: number;
  plaintext: number;
  details: Array<{ service: string; encrypted: boolean }>;
}> {

  const result = {
    total: 0,
    encrypted: 0,
    plaintext: 0,
    details: [] as Array<{ service: string; encrypted: boolean }>
  };

  try {
    // Récupère toutes les clés API
    const { data: apiKeys, error } = await supabase
      .from('api_keys')
      .select('service, api_key');

    if (error) {
      console.error('❌ Erreur lors de la récupération des clés API:', error);
      return result;
    }

    if (!apiKeys || apiKeys.length === 0) {
      return result;
    }

    result.total = apiKeys.length;

    // Vérifie chaque clé
    for (const apiKey of apiKeys) {
      const encrypted = await isKeyEncrypted(apiKey.api_key);
      
      result.details.push({
        service: apiKey.service,
        encrypted
      });

      if (encrypted) {
        result.encrypted++;
      } else {
        result.plaintext++;
      }
    }

    // Affiche le résumé

    if (result.plaintext > 0) {
      result.details
        .filter(detail => !detail.encrypted)
        .forEach(detail => {
        });
    }

    return result;

  } catch (error) {
    console.error('❌ Erreur lors de la vérification:', error);
    return result;
  }
}

/**
 * Fonction pour exécuter la migration si nécessaire
 */
export async function runMigrationIfNeeded(): Promise<void> {

  try {
    // Vérifie d'abord l'état actuel
    const status = await checkEncryptionStatus();

    if (status.plaintext === 0) {
      return;
    }


    // Lance la migration
    const migrationResult = await migrateApiKeysToEncryption();

    if (migrationResult.errors === 0) {
    } else {
    }

  } catch (error) {
    console.error('❌ Erreur lors de la migration automatique:', error);
  }
}

// ============================================================================
// EXPORT DES FONCTIONS
// ============================================================================

export default {
  migrateApiKeysToEncryption,
  checkEncryptionStatus,
  runMigrationIfNeeded
};
